<template>
  <div class="avatar-popup-shell">
    <div v-if="!bridgeReady" class="avatar-popup-shell__loading">
      正在连接主窗口...
    </div>
    <template v-else>
      <div class="avatar-popup-shell__interface">
        <div class="avatar-popup-shell__stage">
          <AvatarPanel
            full-width
            :is-agent-speaking="bridgeState.isAgentSpeaking"
            :is-agent-thinking="bridgeState.isAgentThinking"
            :avatar-image="bridgeState.avatarImage"
          />
        </div>

        <div class="status-indicator" :class="bridgeState.connectStatus">
          {{ statusLabel }}
        </div>

        <div class="avatar-popup-shell__controls">
          <ControlsPanel
            :is-connected="bridgeState.isConnected"
            :is-connecting="bridgeState.isConnecting"
            :is-microphone-muted="bridgeState.isMicrophoneMuted"
            :is-volume-muted="bridgeState.isVolumeMuted"
            :volume="bridgeState.volume"
            :is-portrait-mode="false"
            @connect="invoke('connect')"
            @disconnect="invoke('disconnect')"
            @toggle-microphone="invoke('toggleMicrophone')"
            @toggle-volume-mute="invoke('toggleVolumeMute')"
            @volume-change="(value) => invoke('volumeChange', value)"
            @interrupt="invoke('interrupt')"
            @summarize="invoke('summarize')"
            @logout="invoke('logout')"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import AvatarPanel from './AvatarPanel.vue';
import ControlsPanel from './ControlsPanel.vue';
import {
  ConnectionBridge,
  ConnectionBridgeState,
  getConnectionBridge,
} from '@/utils/connectionBridge';

const defaultState = (): ConnectionBridgeState => ({
  isConnected: false,
  isConnecting: false,
  isMicrophoneMuted: false,
  isVolumeMuted: false,
  volume: 100,
  isPortraitMode: false,
  isWindowsSplit: true,
  isAgentSpeaking: false,
  isAgentThinking: false,
  avatarImage: '/uploads/xingxiaonge6c.gif',
  connectStatus: 'disconnected',
});

export default defineComponent({
  name: 'AvatarPopupShell',
  components: {
    AvatarPanel,
    ControlsPanel,
  },
  setup() {
    const bridgeReady = ref(false);
    const bridgeState = ref<ConnectionBridgeState>(defaultState());
    let syncTimer: number | null = null;

    const statusLabel = computed(() => {
      if (bridgeState.value.connectStatus === 'connected') return '在线';
      if (bridgeState.value.connectStatus === 'reconnecting') return '重连中';
      return '离线';
    });

    const syncFromBridge = (): void => {
      const bridge = getConnectionBridge();
      if (!bridge) {
        bridgeReady.value = false;
        window.close();
        return;
      }

      bridgeReady.value = true;
      bridgeState.value = bridge.getState();
    };

    const invoke = (
      method:
        | 'connect'
        | 'disconnect'
        | 'toggleMicrophone'
        | 'toggleVolumeMute'
        | 'volumeChange'
        | 'interrupt'
        | 'summarize'
        | 'toggleSplitWindows'
        | 'logout',
      payload?: number
    ): void => {
      const bridge = getConnectionBridge() as ConnectionBridge | null;
      if (!bridge) {
        return;
      }

      if (method === 'volumeChange') {
        bridge.volumeChange(payload ?? 100);
      } else {
        bridge[method]();
      }

      window.setTimeout(syncFromBridge, 0);
    };

    const handleSplitShortcut = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 's') {
        return;
      }

      event.preventDefault();
      invoke('toggleSplitWindows');
    };

    onMounted(() => {
      syncFromBridge();
      syncTimer = window.setInterval(syncFromBridge, 200);
      window.addEventListener('keydown', handleSplitShortcut);
    });

    onUnmounted(() => {
      if (syncTimer !== null) {
        window.clearInterval(syncTimer);
      }
      window.removeEventListener('keydown', handleSplitShortcut);
    });

    return {
      bridgeReady,
      bridgeState,
      statusLabel,
      invoke,
    };
  },
});
</script>

<style scoped>
.avatar-popup-shell {
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: var(--bg-gradient-primary);
  overflow: hidden;
}

.avatar-popup-shell__loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 16px;
}

.avatar-popup-shell__interface {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
  background: var(--bg-card);
  backdrop-filter: var(--blur-light);
  -webkit-backdrop-filter: var(--blur-light);
  border-radius: 0;
  padding: 10px 12px 12px;
  box-sizing: border-box;
}

.avatar-popup-shell__stage {
  flex: 1;
  min-height: 0;
  display: flex;
}

.avatar-popup-shell__controls {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 12px 8px;
  box-sizing: border-box;
}

.avatar-popup-shell__controls :deep(.controls) {
  width: auto;
  max-width: 100%;
  margin-top: 0;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
}

.avatar-popup-shell__controls :deep(.controls.portrait-mode) {
  position: static;
  left: auto;
  top: auto;
  transform: none;
  flex-direction: row;
}

.avatar-popup-shell__controls :deep(.extra-buttons.portrait) {
  flex-direction: row;
  margin-top: 0;
}

.avatar-popup-shell__controls :deep(.glass-button-bar.portrait) {
  flex-direction: row;
}

.status-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  background: rgba(255, 255, 255, 0.9);
  color: var(--text-secondary);
  z-index: 10;
  border: 1px solid rgba(0, 184, 217, 0.2);
}

.status-indicator.connected {
  color: var(--success-color);
  background: rgba(230, 255, 250, 0.85);
  border-color: rgba(0, 185, 107, 0.25);
}

.status-indicator.reconnecting {
  color: #d97706;
  background: rgba(255, 247, 230, 0.9);
  border-color: rgba(245, 158, 11, 0.28);
}
</style>
